import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { resolveAssetUrl } from '../utils/assets';

const isInlineOrBlobUrl = (value = '') => /^(data:|blob:)/i.test(value);

const shouldFetchWithAuth = (url = '') => {
  if (!url || isInlineOrBlobUrl(url)) return false;
  try {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
    const parsed = new URL(url, fallbackOrigin);
    const base = new URL(api?.defaults?.baseURL || fallbackOrigin, fallbackOrigin);
    return parsed.origin === base.origin && /^\/files$/i.test(parsed.pathname.replace(/\/+$|\/api\/files$/i, '/files'));
  } catch {
    return false;
  }
};

export default function useAuthenticatedAssetUrl(value, options = {}) {
  const authenticatedPath = options?.path || "";
  const resolvedUrl = useMemo(() => authenticatedPath || resolveAssetUrl(value), [authenticatedPath, value]);
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    let active = true;
    let currentObjectUrl = '';
    setObjectUrl('');

    const mustFetch = Boolean(authenticatedPath) || shouldFetchWithAuth(resolvedUrl);
    if (!resolvedUrl || !mustFetch) {
      return undefined;
    }

    api.get(resolvedUrl, { responseType: 'blob' })
      .then((response) => {
        currentObjectUrl = URL.createObjectURL(response.data);
        if (!active) {
          URL.revokeObjectURL(currentObjectUrl);
          return;
        }
        setObjectUrl(currentObjectUrl);
      })
      .catch(() => {
        if (active) setObjectUrl('');
      });

    return () => {
      active = false;
      if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
    };
  }, [resolvedUrl, authenticatedPath]);

  if (!resolvedUrl) return '';
  if (isInlineOrBlobUrl(resolvedUrl)) return resolvedUrl;
  return objectUrl || ((authenticatedPath || shouldFetchWithAuth(resolvedUrl)) ? '' : resolvedUrl);
}
